import { isEmpty } from 'rambda';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTypedHash, useTypedSearchParams } from 'react-router-typesafe-routes';
import TabList from '@/components/common/tablist';
import AppHeader from '@/components/layouts/page-layout/app-header';
import AppHeaderMenu from '@/components/layouts/page-layout/app-header-menu';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import SearchBar from '@/components/search/search-bar';
import SearchFriendResultList from '@/components/search/search-friend-result-list';
import SearchGroupResultList from '@/components/search/search-group-result-list';
import SearchHistory from '@/components/search/search-history';
import SearchMessageResultList from '@/components/search/search-message-result-list';
import useGlobalSearch, { type GlobalSearchReducerActionType } from '@/hooks/search/use-global-search';
import { useIsDesktop } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import useSearchHistoryStore from '@/stores/search-history';

export default function GlobalSearchRoute() {
  const { t } = useTranslation();
  const [{ keyword: searchKeyword = '' }, setSearchParams] = useTypedSearchParams(ROUTES.SEARCH);
  const hash = useTypedHash(ROUTES.SEARCH);
  const [filterType, setFilterType] = useState<GlobalSearchReducerActionType>(hash ?? 'all');
  const { messages, groupMembers, friends, isLoading } = useGlobalSearch(searchKeyword);

  const isDesktop = useIsDesktop();

  const filterOptions = useMemo(() => {
    const tabs = new Set<string>(['all']);

    if (!isEmpty(friends)) {
      tabs.add('friends');
    }
    if (!isEmpty(groupMembers)) {
      tabs.add('groups');
    }
    if (!isEmpty(messages)) {
      tabs.add('messages');
    }

    return tabOptions.filter(({ key }) => tabs.has(key));
  }, [friends, groupMembers, messages]);

  const handleSearch = (inputValue: string) => {
    if (!isEmpty(inputValue)) {
      useSearchHistoryStore.getState().pushSearchHistory(inputValue);
      setSearchParams({ keyword: inputValue });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handleChangeTab = (key: GlobalSearchReducerActionType) => {
    setFilterType(key);
    const href = ROUTES.SEARCH.$buildPath({
      searchParams: { keyword: searchKeyword },
      hash: key === 'all' ? undefined : key,
    });
    history.replaceState(null, '', href);
  };

  return (
    <PageLayout
      constrainHeight
      enablePullToRefresh={false}
      className='gap-y-0 max-w-100vw flex-1 overflow-hidden'
      title={t('search.placeholder')}
      headerConfig={{
        elementBeforeNav: isDesktop && (
          <AppHeader
            actions={<AppHeaderMenu />}
            className='flex-1 text-left text-2xl font-bold'>
            {t('nav.messages')}
          </AppHeader>
        ),
        className: 'mt-1 mb-1',
        navigateBack: !isDesktop,
        actions: '\u200b', // non-spacing character
        headerLabel: (
          <SearchBar
            keyword={searchKeyword}
            onSearch={handleSearch}
          />
        ),
        elementAfterNav: searchKeyword && !isLoading && (
          <TabList
            tabs={filterOptions}
            defaultActiveTab={filterType}
            onClickTab={handleChangeTab}
          />
        ),
      }}>
      {searchKeyword ? (
        <LoadingLayout
          promise={isLoading}
          overlay>
          {() => (
            <div className='flex flex-col w-full gap-4 overflow-y-auto pb-safe flex-1'>
              {[friends, groupMembers, messages].flat().length === 0 ? (
                <div className='flex justify-center items-center flex-1'>
                  <p className='text-secondary text-body1 '>{t('search.no-result')}</p>
                </div>
              ) : (
                <>
                  {['friends', 'all'].includes(filterType) && (
                    <SearchFriendResultList
                      friends={friends}
                      keyword={searchKeyword}
                    />
                  )}
                  {['groups', 'all'].includes(filterType) && (
                    <SearchGroupResultList
                      groups={groupMembers}
                      keyword={searchKeyword}
                    />
                  )}
                  {['messages', 'all'].includes(filterType) && (
                    <SearchMessageResultList
                      results={messages}
                      keyword={searchKeyword}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </LoadingLayout>
      ) : (
        <SearchHistory onSearch={handleSearch} />
      )}
    </PageLayout>
  );
}

const tabOptions: Array<{ key: GlobalSearchReducerActionType; label: string }> = [
  { key: 'all', label: 'common.all' },
  { key: 'friends', label: 'search.contacts' },
  { key: 'groups', label: 'search.groups' },
  { key: 'messages', label: 'search.chats' },
];
